import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "remote-filters s-my-4" }
const _hoisted_2 = { class: "remote-filters__bar flex flex-column gap-4" }
const _hoisted_3 = {
  class: "remote-filters__header flex w-50 gap-4",
  "data-test": "remote-filters-header"
}
const _hoisted_4 = { class: "flex flex-col gap-2" }
const _hoisted_5 = { class: "gap-8" }
const _hoisted_6 = { class: "gap-4 flex flex-column" }

import CoreButton from 'frontcore/components/CoreButton.vue'
import i18n from 'frontcore/i18n'
import RemoteFiltersItem from 'frontcore/presentation/remote-filters/views/RemoteFiltersItem.vue'
import RemoteFiltersSearch from 'frontcore/presentation/remote-filters/views/RemoteFiltersSearch.vue'
import RemoteFiltersSelectedChips from 'frontcore/presentation/remote-filters/views/RemoteFiltersSelectedChips.vue'
import SidebarModal from 'frontcore/views/modals/SidebarModal/SidebarModal.vue'
import useRemoteFiltersStore from 'frontcore/presentation/remote-filters/data/use-remote-filters-store'
import { computed, onUnmounted, ref, watch } from 'vue'
import { eventBus } from 'frontcore/communications/eventBus'
import { PORTAL_LEVELS } from 'frontcore/constants/constants'
import {
  RemoteFiltersFieldType,
  RemoteFiltersStoreConfiguration,
} from 'frontcore/presentation/remote-filters/domain/types'
import { useRoute, useRouter } from 'vue-router'

type Props = {
  filterURL: string
  storeConfiguration?: RemoteFiltersStoreConfiguration
}
type Emits = (e: 'didLoad') => void

export default /*@__PURE__*/_defineComponent({
  __name: 'RemoteFilters',
  props: {
    filterURL: {},
    storeConfiguration: {}
  },
  emits: ["didLoad"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const route = useRoute()
const router = useRouter()

const isLoading = ref(true)

const filtersStore = computed(() => {
  return useRemoteFiltersStore(props.filterURL, props.storeConfiguration, route, router)
})

watch(
  () => route.name,
  () => {
    (async () => {
      isLoading.value = true
      await filtersStore.value.initialFetch()
      emit('didLoad')
      isLoading.value = false
    })()
  },
  { immediate: true }
)

onUnmounted(() => {
  eventBus.off('openRemoteFilters')
})

const isSideBarFilterVisible = ref(false)

const toggleSideBarVisibility = () => {
  if (isLoading.value) { return }
  setTimeout(() => {
    isSideBarFilterVisible.value = !isSideBarFilterVisible.value
  })
}
eventBus.on('openRemoteFilters', toggleSideBarVisibility)

const closeSideBar = () => {
  filtersStore.value.clearChanges()
  toggleSideBarVisibility()
}

const filterConfigs = computed(() => {
  return filtersStore.value.moduleRemoteFiltersConfigurations.filter(filter => {
    const hasRelatedFilter = filtersStore.value.moduleRemoteFiltersConfigurations.some(availableFilters => filter.relatedFilters.includes(availableFilters.filterId))
    return filter.filterType !== RemoteFiltersFieldType.SEARCH && !hasRelatedFilter
  })
})

const { t } = i18n.global

const header = {
  dataTest: 'remote-filters-header',
  iconName: 'FilterAlt',
  onCloseHandler: toggleSideBarVisibility,
  title: t('common.messages.filters'),
}

const viewResults = () => {
  toggleSideBarVisibility()
}

const resetFilters = () => {
  filtersStore.value.clearChanges()
  toggleSideBarVisibility()
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(RemoteFiltersSearch, {
          filterURL: _ctx.filterURL,
          storeConfiguration: _ctx.storeConfiguration,
          class: "flex-grow-1"
        }, null, 8, ["filterURL", "storeConfiguration"]),
        _createVNode(CoreButton, {
          buttonType: "secondary",
          "data-test": "available-filters-button",
          iconName: "FilterAlt",
          disabled: isLoading.value,
          label: _unref(t)('common.messages.filters'),
          onClick: toggleSideBarVisibility
        }, null, 8, ["disabled", "label"])
      ]),
      (filtersStore.value.remoteFilterStoreID)
        ? (_openBlock(), _createBlock(RemoteFiltersSelectedChips, {
            key: 0,
            filterStoreID: filtersStore.value.remoteFilterStoreID,
            onSelectFilterItem: toggleSideBarVisibility
          }, null, 8, ["filterStoreID"]))
        : _createCommentVNode("", true)
    ]),
    (isSideBarFilterVisible.value)
      ? (_openBlock(), _createBlock(SidebarModal, {
          key: 0,
          portalTarget: _unref(PORTAL_LEVELS).THREE,
          header: header,
          closeButtonLabel: _unref(t)('common.close'),
          dataTest: "sidebar-modal-filters",
          modalClass: "col-start-1 col-span-12 sm:col-start-9 sm:col-span-4 xxl:col-start-10 xxl:col-span-3",
          onClose: closeSideBar
        }, {
          body: _withCtx(() => [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("div", _hoisted_6, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filterConfigs.value, (configuration) => {
                    return (_openBlock(), _createBlock(RemoteFiltersItem, {
                      key: configuration.filterId,
                      configuration: configuration,
                      moduleRemoteFiltersConfigurations: filtersStore.value.moduleRemoteFiltersConfigurations,
                      filterStoreID: filtersStore.value.remoteFilterStoreID
                    }, null, 8, ["configuration", "moduleRemoteFiltersConfigurations", "filterStoreID"]))
                  }), 128))
                ])
              ])
            ])
          ]),
          footer: _withCtx(() => [
            _createVNode(CoreButton, {
              dataTest: "remote-filter-commit-button",
              class: "tw-grow",
              label: _unref(t)('common.messages.see_results'),
              onClick: viewResults
            }, null, 8, ["label"]),
            _createVNode(CoreButton, {
              buttonType: "destructive-ghost",
              class: "tw-grow",
              iconName: "RefreshFill",
              dataTest: "remote-filter-reset-button",
              label: _unref(t)('common.messages.reset'),
              onClick: resetFilters
            }, null, 8, ["label"])
          ]),
          _: 1
        }, 8, ["portalTarget", "closeButtonLabel"]))
      : _createCommentVNode("", true)
  ]))
}
}

})